/**
 * Every customer save goes through here, so the "Saved" pill (ui/SavePill.js,
 * docs/design/HANDOFF.md "Saved toast") can say what is happening:
 *
 *   saved     a save went through (shown about 2.5 s)
 *   saving    a save has taken longer than 1 s
 *   failed    a save failed twice; retrying (every few seconds, then every 30 s)
 *   offline   still failing after about a minute ("kept on this device")
 *
 * trackSave(run, { key, kind, payload }) runs `run()` (returns a promise).
 * A save with a `kind` and `payload` is also kept in local storage until it
 * succeeds, so a closed tab doesn't lose it: on the next visit
 * flushSavedQueue() sends it again through the handler registered for that
 * kind (registerSaveKind). A newer save with the same `key` replaces an older
 * one that hasn't gone through yet.
 *
 * notifySaved() shows "Saved" for something that saved on its own (an upload
 * that finished). Uploads still in flight are counted with setUploadsInFlight,
 * which is what makes the browser ask before leaving the page.
 *
 * A failure whose error has `permanent: true` (the server refused it) is
 * dropped instead of retried.
 */

const QUEUE_KEY = "md_unsaved_changes";
const SAVING_AFTER_MS = 1000;
const SAVED_FOR_MS = 2500;
const FADE_MS = 300;
const QUIET_RETRY_MS = 2000;
const RETRY_STEPS_MS = [5000, 10000, 20000, 30000];
const OFFLINE_AFTER_MS = 60000;

const listeners = new Set();
const handlers = new Map();
// key -> { key, run, kind, payload, attempts, firstFailedAt, timer }
const pending = new Map();
let state = { status: "idle" };
let savedTimer = null;
let savingTimer = null;
let uploadsInFlight = 0;
let seq = 0;

function emit(next) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function subscribeSaveStatus(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function getSaveStatus() {
  return state;
}

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeQueue(queue) {
  try {
    if (Object.keys(queue).length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    else localStorage.removeItem(QUEUE_KEY);
  } catch {
    // Storage full or blocked: the in-memory retry still runs.
  }
}

function keepOnDevice(entry) {
  if (!entry.kind) return;
  const queue = readQueue();
  queue[entry.key] = { kind: entry.kind, payload: entry.payload, at: Date.now() };
  writeQueue(queue);
}

function dropFromDevice(key) {
  const queue = readQueue();
  if (queue[key]) {
    delete queue[key];
    writeQueue(queue);
  }
}

function failing() {
  return [...pending.values()].filter((e) => e.firstFailedAt);
}

/** What the pill should show now. */
function refresh() {
  const failed = failing();
  if (failed.length) {
    clearTimeout(savedTimer);
    const oldest = Math.min(...failed.map((e) => e.firstFailedAt));
    emit({ status: Date.now() - oldest >= OFFLINE_AFTER_MS ? "offline" : "failed" });
    return;
  }
  if (pending.size === 0 && (state.status === "failed" || state.status === "offline" || state.status === "saving")) {
    showSaved();
  }
}

function showSaved() {
  clearTimeout(savingTimer);
  clearTimeout(savedTimer);
  emit({ status: "saved", at: Date.now() });
  savedTimer = setTimeout(() => {
    if (state.status !== "saved") return;
    emit({ status: "saved", fading: true });
    savedTimer = setTimeout(() => {
      if (state.status === "saved") emit({ status: "idle" });
    }, FADE_MS);
  }, SAVED_FOR_MS);
}

function attempt(entry) {
  const mine = ++seq;
  entry.seq = mine;
  clearTimeout(entry.timer);
  return Promise.resolve()
    .then(() => entry.run())
    .then(
      (result) => {
        if (pending.get(entry.key)?.seq !== mine) return result; // replaced by a newer save
        pending.delete(entry.key);
        dropFromDevice(entry.key);
        if (failing().length) refresh();
        else if (pending.size === 0) showSaved();
        return result;
      },
      (error) => {
        if (pending.get(entry.key)?.seq !== mine) return undefined;
        if (error?.permanent) {
          // The server refused it; retrying would only loop.
          console.error(`Save "${entry.key}" was refused:`, error?.message || error);
          pending.delete(entry.key);
          dropFromDevice(entry.key);
          if (failing().length) refresh();
          else if (!pending.size) {
            clearTimeout(savingTimer);
            emit({ status: "idle" });
          }
          return undefined;
        }
        entry.attempts += 1;
        console.warn(`Save "${entry.key}" failed (attempt ${entry.attempts}):`, error?.message || error);
        if (entry.attempts === 1) {
          // One quiet retry first: a blip shouldn't put up a warning.
          entry.timer = setTimeout(() => attempt(entry), QUIET_RETRY_MS);
          return undefined;
        }
        if (!entry.firstFailedAt) entry.firstFailedAt = Date.now();
        const wait = RETRY_STEPS_MS[Math.min(entry.attempts - 2, RETRY_STEPS_MS.length - 1)];
        entry.timer = setTimeout(() => attempt(entry), wait);
        refresh();
        // The "kept on this device" wording takes over after a minute even between retries.
        setTimeout(refresh, Math.max(0, entry.firstFailedAt + OFFLINE_AFTER_MS - Date.now()) + 50);
        return undefined;
      }
    );
}

/**
 * Run a save and report it on the pill. Resolves with the save's result, or
 * undefined if it failed (it keeps retrying in the background).
 */
export function trackSave(run, { key, kind = null, payload = null } = {}) {
  const entryKey = key || `save-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const previous = pending.get(entryKey);
  if (previous) clearTimeout(previous.timer);
  const entry = { key: entryKey, run, kind, payload, attempts: 0, firstFailedAt: null, timer: null };
  pending.set(entryKey, entry);
  keepOnDevice(entry);
  clearTimeout(savingTimer);
  savingTimer = setTimeout(() => {
    if (pending.size && !failing().length) emit({ status: "saving" });
  }, SAVING_AFTER_MS);
  return attempt(entry);
}

/** Retry every failing save now ("Try now"). */
export function retryNow() {
  failing().forEach((entry) => attempt(entry));
}

/** "Saved" for a save that happened outside trackSave (a finished upload). */
export function notifySaved() {
  if (failing().length) return;
  showSaved();
}

/** How to re-send a kept change on the next visit: registerSaveKind("bookEdit", (payload) => bookEdit(payload)). */
export function registerSaveKind(kind, handler) {
  handlers.set(kind, handler);
}

/** Send changes kept on the device by an earlier visit. */
export function flushSavedQueue() {
  const queue = readQueue();
  Object.entries(queue).forEach(([key, { kind, payload }]) => {
    const handler = handlers.get(kind);
    if (!handler || pending.has(key)) return;
    trackSave(() => handler(payload), { key, kind, payload });
  });
}

/** Photo uploads in flight: the only thing that makes leaving the page ask first. */
export function setUploadsInFlight(count) {
  uploadsInFlight = Math.max(0, count);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if (uploadsInFlight > 0) {
      event.preventDefault();
      event.returnValue = "";
    }
  });
  // Back online: don't wait for the next retry.
  window.addEventListener("online", () => retryNow());
}

/** Tests only. */
export function __resetSaveTracker() {
  pending.forEach((e) => clearTimeout(e.timer));
  pending.clear();
  clearTimeout(savedTimer);
  clearTimeout(savingTimer);
  handlers.clear();
  uploadsInFlight = 0;
  state = { status: "idle" };
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    // ignore
  }
}
